import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import axios from 'axios'

import { ProjectContext } from '@/contexts/ProjectContext'
import type {
  Project,
  ProjectContextValue,
  ProjectLoadStatus,
} from '@/contexts/ProjectContext'
import { projectService } from '@/services/projectService'
import { logger } from '@/utils/logger'

const log = logger.scoped('ProjectProvider')

const STORAGE_KEY = 'stagehand.projectId'

// localStorage access throws under blocked-storage policies (private modes,
// enterprise cookie blocks, sandboxed webviews); selection persistence is a
// nicety, so degrade to the default rather than crash the provider.
function readSavedProjectId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function saveProjectId(id: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, id)
  } catch (err) {
    log.warn('Could not persist project selection', { error: err })
  }
}

export function ProjectProvider({ children }: { children: ReactNode }) {
  const [projects, setProjects] = useState<Project[]>([])
  const [currentProject, setCurrentProject] = useState<Project | null>(null)
  const [status, setStatus] = useState<ProjectLoadStatus>('loading')

  // Projects come from the Project table via the API (GET /api/project).
  // The app never creates them — rows are seeded locally and will arrive via
  // the Boomi integration in deployed environments.
  useEffect(() => {
    const controller = new AbortController()

    async function loadProjects() {
      try {
        const dtos = await projectService.getActive(controller.signal)
        const loaded: Project[] = dtos.map((dto) => ({
          id: dto.projectNumber,
          name: dto.projectName,
        }))
        const savedId = readSavedProjectId()
        const resolved =
          loaded.find((p) => p.id === savedId) ?? loaded[0] ?? null
        setProjects(loaded)
        setCurrentProject(resolved)
        // Persist the resolution whenever it differs from what was saved, so a
        // stale id can't linger and silently re-select a project that later
        // returns to the list. Displayed selection and persisted selection
        // must always agree.
        if (resolved && resolved.id !== savedId) {
          saveProjectId(resolved.id)
        }
        setStatus('loaded')
      } catch (err) {
        if (axios.isCancel(err)) return
        log.error('Failed to load projects', { error: err })
        setStatus('error')
      }
    }

    loadProjects()
    return () => controller.abort()
  }, [])

  const value = useMemo<ProjectContextValue>(
    () => ({
      projects,
      currentProject,
      status,
      setProject: (id: string) => {
        const project = projects.find((p) => p.id === id)
        if (!project) return
        setCurrentProject(project)
        saveProjectId(project.id)
      },
    }),
    [projects, currentProject, status],
  )

  return (
    <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
  )
}
